import { RatingsFilterModelComparator } from './comparators/ratings-filter-model-comparator';
import { CalendarGridColumnName } from '../../../grid/types/column.types';
import {
  IFilterComparator,
  RatingsFilterModel,
  ConfigureOptionsParams,
  FilterOptions,
  FilterPassParams,
  AppliedFilterItem
} from '../filter-types';
import { FilterResultFacade } from '../filter-result-facade';
import {
  PredefinedEnhancementFilterValue,
  RatingsFilterValue,
  RATINGS,
  getRatingFilterOptionLabel,
  getEnhancementFilterOptionLabel
} from '../../../grid/utils/filter/ratings/ratings-filters';
import {
  CalendarFilter,
  CalendarFiltersState
} from '../../../state/calendar-filter/state';
import { EnhancementDealLevelFilter } from '../../../grid/utils/filter/ratings/enhancement-deal-level-filter';
import { RatingDealLevelFilter } from '../../../grid/utils/filter/ratings/ratings-deal-level-filter';
import { FilterHandlerBase } from './base/filter-handler-base';
import {
  FilterChange,
  FilterClear
} from '../../../state/calendar-filter/actions';

export class RatingsFilterHandler extends FilterHandlerBase {
  private RATINGS_LABEL = 'Ratings';
  private ENCHANCEMENT_LABEL = 'Enhancement';
  private comparator: IFilterComparator<RatingsFilterModel>;

  get name(): string {
    return CalendarGridColumnName.Ratings;
  }

  get isInclusiveExclusive(): boolean {
    return false;
  }

  configureFilterOptions(params: ConfigureOptionsParams): void {
    const ratingsFilter = params.api.getFilterInstance(
      CalendarGridColumnName.Ratings
    );
    const ratingsFilterInstance = ratingsFilter.getFrameworkComponentInstance();
    ratingsFilterInstance.enhancements = [
      ...params.filterOptions.enhancements
    ].sort();
  }

  getFilterOptions(filterOptions: FilterOptions): any {
    return filterOptions.enhancements;
  }

  hasValidDiff(options: FilterOptions, grid: any, store: any): boolean {
    return this.getValidDiffComparator(options).hasValidDiff(grid, store);
  }

  getValidDiffComparator(
    options: FilterOptions
  ): IFilterComparator<RatingsFilterModel> {
    if (!this.comparator) {
      this.comparator = new RatingsFilterModelComparator();
    }
    this.comparator.applyFilterOptions(this.getFilterOptions(options));
    return this.comparator;
  }

  doFilterPass(params: FilterPassParams, result: FilterResultFacade): boolean {
    const model = params.filters[this.name].model;
    const deal = params.deal;

    const enhancementFilter = new EnhancementDealLevelFilter(model, result);
    const ratingsFilter = new RatingDealLevelFilter(model, result, this.name);

    return (
      enhancementFilter.applyFilter(deal) && ratingsFilter.applyFilter(deal)
    );
  }

  getAppliedFiltersItems(filter: CalendarFilter): AppliedFilterItem[] {
    if (!!filter && !!filter.model) {
      const result = [];
      const newItem: (arg0, arg1, arg2) => unknown = this
        .createAppliedFilterItem;
      if (filter.model.ratingsRadio === RatingsFilterValue.notRated) {
        result.push(
          newItem(
            filter,
            this.RATINGS_LABEL,
            getRatingFilterOptionLabel(RatingsFilterValue.notRated)
          )
        );
      } else {
        const ratingsValue = `${RATINGS[filter.model.minValue]} thru ${
          RATINGS[filter.model.maxValue]
        }`;
        switch (filter.model.radio) {
          case PredefinedEnhancementFilterValue.all:
            result.push(newItem(filter, this.RATINGS_LABEL, ratingsValue));
            break;
          case PredefinedEnhancementFilterValue.enhanced:
            result.push(
              newItem(
                filter,
                this.ENCHANCEMENT_LABEL,
                getEnhancementFilterOptionLabel(
                  PredefinedEnhancementFilterValue.enhanced
                )
              )
            );
            break;
          case PredefinedEnhancementFilterValue.unenhanced:
            result.push(newItem(filter, this.RATINGS_LABEL, ratingsValue));
            result.push(
              newItem(
                filter,
                this.ENCHANCEMENT_LABEL,
                getEnhancementFilterOptionLabel(
                  PredefinedEnhancementFilterValue.unenhanced
                )
              )
            );
            break;
          default:
            result.push(
              this.createAppliedFilterItem(
                filter,
                this.ENCHANCEMENT_LABEL,
                filter.model.radio
              )
            );
        }
      }
      return result;
    }
    return [];
  }

  createAppliedFilterItem(
    filter: CalendarFilter,
    label: string,
    value: string
  ): AppliedFilterItem {
    return {
      name: filter.name,
      label: label,
      filterLabel: label,
      filterValue: value
    };
  }

  filterClear(
    filterChange: FilterClear,
    state: CalendarFiltersState
  ): FilterChange {
    const result = { name: this.name, value: undefined };
    if (filterChange.name === this.name) {
      const filterName = filterChange.name;
      const subFilterName = filterChange.subFilterName;
      const filter = { ...state[filterName] };
      if (subFilterName === this.RATINGS_LABEL) {
        filter.model.ratingsRadio = undefined;
      } else if (subFilterName === this.ENCHANCEMENT_LABEL) {
        switch (filter.model.radio) {
          case PredefinedEnhancementFilterValue.all:
            filter.model.ratingsRadio = undefined;
            filter.model.radio = undefined;
            break;
          case PredefinedEnhancementFilterValue.enhanced:
            filter.model.ratingsRadio = undefined;
            filter.model.radio = undefined;
            break;
          case PredefinedEnhancementFilterValue.unenhanced:
            if (
              filter.model.minValue === 0 &&
              filter.model.maxValue === RATINGS.length - 1
            ) {
              filter.model.radio = undefined;
              filter.model.ratingsRadio = undefined;
            } else {
              const ratingsValue = `${RATINGS[filter.model.minValue]} thru ${
                RATINGS[filter.model.maxValue]
              }`;
              filter.model.stringModel = ratingsValue;
              filter.model.radio = PredefinedEnhancementFilterValue.all;
              filter.model.ratingsRadio = RatingsFilterValue.all;
              filter.timestamp = Date.now();
            }
            break;
          default:
            filter.model.ratingsRadio = undefined;
            filter.model.radio = undefined;
        }
      } else {
        filter.model.ratingsRadio = undefined;
        filter.model.radio = undefined;
      }
      result.value =
        filter.model.ratingsRadio === undefined &&
        filter.model.radio === undefined
          ? undefined
          : filter;
    }
    return result;
  }
}
